import React, { useState, useEffect } from 'react';
import { useDataContext } from '../../design-system/src/context/page-context-provider';
import LoadingSpinnerImgContainer from '../../design-system/src/components/loading-spinner/loading-spinner-img-container';
import OpeningParagraph from '@components/masthead/opening-paragraph';
import styled from '@emotion/styled/macro';
import TileBlock from '@blocks/tile-block/tile-block';
import vars from '@variables';
import { convertCreatedAtOverrideDate } from '../../utils/date';
import { defineResponsiveGridColumns } from '@utils/utils';
import { TranslateUnconnected } from '../../design-system/src/utils/translation-functions';

interface JobVacanciesProps {
  className?: string;
}

interface CurrentVacanciesProps {
  JobTitle?: string;
  Reference?: string;
  JobBordUrl?: string;
}

const JobVacancies: React.FC<JobVacanciesProps> = ({ className }) => {
  const [currentVacancies, setCurrentVacancies] = useState<
    CurrentVacanciesProps[] | null
  >(null);

  const { pageContext } = useDataContext();
  const staticTranslations = pageContext?.staticTranslations;
  useEffect(() => {
    const url = 'https://api.peoplehr.net/Vacancy';
    const data = {
      APIKey: process.env.GATSBY_PEOPLE_HR_API_KEY,
      Action: 'GetAllVacancies',
    };
    const fetchCSIData = {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json; charset=UTF=8',
      }),
      body: JSON.stringify(data),
    };

    const getRssData = async () => {
      try {
        const response = await fetch(url, fetchCSIData);
        const json = await response.json();
        const allVacancies = json?.Result;

        const date = new Date();
        const currentDate = convertCreatedAtOverrideDate(date);

        if (allVacancies) {
          // Filter current vacancies on date and !internal.
          const filteredVacancies = (vacancyType, currentDate) => {
            const displayedVacancies = allVacancies
              .filter(vacancy => {
                const vacancyClosingDate = new Date(vacancy.ClosingDate);
                const convertDate =
                  convertCreatedAtOverrideDate(vacancyClosingDate);
                return (
                  vacancy.VacancyType !== vacancyType &&
                  convertDate >= currentDate &&
                  vacancy.Status === 1
                );
              })
              .map(filteredVacancy => {
                return {
                  JobTitle: filteredVacancy.JobTitle,
                  Reference: filteredVacancy.Reference,
                  JobBordUrl: filteredVacancy.JobBordUrl,
                };
              });
            setCurrentVacancies(displayedVacancies);
          };
          filteredVacancies('Internal', currentDate);
        }
      } catch (error) {
        console.log('error', error);
      }
    };

    getRssData();
  }, []);

  if (currentVacancies !== null) {
    if (currentVacancies.length > 0) {
      const tiles = currentVacancies
        .sort((a, b) => (a.JobTitle ?? '').localeCompare(b.JobTitle ?? ''))
        .map(({ Reference: id, JobBordUrl: url, JobTitle: title }) => ({
          renderType: 'standard',
          id,
          url,
          title,
          imageUrl:
            'https://images.ctfassets.net/isq5xwjfoz2m/p7pLaPxORWoFrBm6XMySU/9b962409aa9bfc41006142a8a2466b85/Listing_-_jobs_-_Policy_1.png',
          imageAlt: `Job Vacancy | ${title}`,
        }));

      return (
        <div className={className}>
          <TileBlock
            title={
              staticTranslations
                ? TranslateUnconnected('Current vacancies', staticTranslations)
                : 'Current vacancies'
            }
            description={null}
            cta={null}
            tiles={tiles}
          />
        </div>
      );
    } else {
      return (
        <IndentWrapper>
          <OpeningParagraph
            openingParagraph={
              staticTranslations
                ? TranslateUnconnected(
                    "We don't have any vacancies at the moment. Please come back later to check on future career opportunities.",
                    staticTranslations
                  )
                : "We don't have any vacancies at the moment. Please come back later to check on future career opportunities."
            }
          />
        </IndentWrapper>
      );
    }
  } else {
    return (
      <div className={className}>
        <LoadingSpinnerImgContainer />
      </div>
    );
  }
};

const IndentWrapper = styled.div`
  display: grid;
  width: 100%;
  margin: 0 auto;
  max-width: ${vars.mainMaxWidth};
  column-gap: ${vars.mobileColumnPercentageGap};
  grid-template-columns: ${defineResponsiveGridColumns(4)};
  padding: 0 ${vars.spacingS};

  @media screen and (min-width: ${vars.breakPointTablet}) {
    column-gap: ${vars.standardColumnPercentageGap};
    grid-template-columns: ${defineResponsiveGridColumns(8)};
    padding: 0 ${vars.spacingL};
  }

  @media screen and (min-width: ${vars.breakPointSmallDesktop}) {
    column-gap: ${vars.standardColumnPercentageGap};
    grid-template-columns: ${defineResponsiveGridColumns(12)};
  }

  ${OpeningParagraph} {
    padding-top: 0;
    grid-column: col1-start / col4-end;

    @media screen and (min-width: ${vars.breakPointTablet}) {
      grid-column: col2-start / col7-end;
    }

    @media screen and (min-width: ${vars.breakPointSmallDesktop}) {
      grid-column: col4-start / col9-end;
    }
  }
`;

const StyledJobVacancies = styled(JobVacancies)`
  ${LoadingSpinnerImgContainer} {
    flex-direction: column;
  }
`;

export default StyledJobVacancies;
